import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SearchSelect } from "@/components/shared/SearchSelect";

const STATUSES = ["new", "reviewing", "confirmed", "declined"];

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("reviewing");

  const load = () => api.get("/admin/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (inq) => {
    setSelected(inq);
    setStatus(inq.status);
    setNote(inq.admin_note || "");
  };

  const save = async () => {
    try {
      await api.patch(`/admin/inquiries/${selected.id}`, { status, admin_note: note });
      toast.success("Inquiry updated · guest notified");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const filtered = filter === "all" ? inquiries : inquiries.filter((i) => i.status === filter);

  return (
    <div>
      <p className="eyebrow">Reservations</p>
      <h1 className="mt-3 font-serif text-3xl tracking-tight">Booking Inquiries</h1>
      <p className="mt-2 font-sans text-sm text-[#94A3B8]">Requests are manually reviewed and confirmed. No inventory is auto-booked.</p>

      <div className="mt-8 flex flex-wrap gap-2">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`inquiry-filter-${s}`} className={`rounded-full border px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-all ${filter === s ? "border-gold bg-gold text-[#0A0D14]" : "border-white/10 text-[#94A3B8] hover:text-white"}`}>{s}</button>
        ))}
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-white/5">
        <table className="w-full text-left">
          <thead className="bg-[#18202E]">
            <tr className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
              <th className="px-5 py-3">Reference</th>
              <th className="px-5 py-3">Guest</th>
              <th className="px-5 py-3">Property</th>
              <th className="px-5 py-3">Dates</th>
              <th className="px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((inq) => (
              <tr key={inq.id} onClick={() => open(inq)} data-testid={`inquiry-row-${inq.id}`} className="cursor-pointer border-t border-white/5 transition-colors hover:bg-white/5">
                <td className="px-5 py-4 font-mono text-xs text-gold">{inq.reference}</td>
                <td className="px-5 py-4 font-sans text-sm">{inq.guest_name}<br /><span className="text-xs text-[#94A3B8]">{inq.guest_email}</span></td>
                <td className="px-5 py-4 font-sans text-sm">{inq.property_name}</td>
                <td className="px-5 py-4 font-sans text-xs text-[#94A3B8]">{inq.check_in} → {inq.check_out}<br />{inq.guests} guest(s)</td>
                <td className="px-5 py-4"><StatusBadge status={inq.status} /></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center font-sans text-sm text-[#94A3B8]">No inquiries.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="border-white/10 bg-[#121722] text-[#F1F5F9]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Inquiry {selected?.reference}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 rounded-md border border-white/5 bg-[#0A0D14] p-4 font-sans text-sm">
                <div><p className="text-xs text-[#94A3B8]">Guest</p>{selected.guest_name}</div>
                <div className="min-w-0 break-all"><p className="text-xs text-[#94A3B8]">Email</p>{selected.guest_email}</div>
                <div><p className="text-xs text-[#94A3B8]">Property</p>{selected.property_name}</div>
                <div><p className="text-xs text-[#94A3B8]">Guests</p>{selected.guests}</div>
                <div><p className="text-xs text-[#94A3B8]">Check-in</p>{selected.check_in}</div>
                <div><p className="text-xs text-[#94A3B8]">Check-out</p>{selected.check_out}</div>
                {selected.special_requests && <div className="col-span-2"><p className="text-xs text-[#94A3B8]">Special requests</p>{selected.special_requests}</div>}
              </div>
              <div>
                <label className="eyebrow mb-2 block">Status</label>
                <SearchSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="crm-inquiry-status-select" className="w-full rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2.5 font-sans text-sm focus:border-gold focus:outline-none">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </SearchSelect>
              </div>
              <div>
                <label className="eyebrow mb-2 block">Note to guest</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="inquiry-note" className="w-full rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2.5 font-sans text-sm focus:border-gold focus:outline-none" />
              </div>
              <button onClick={save} data-testid="inquiry-save" className="w-full rounded-full bg-gold px-6 py-3 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14] hover:bg-gold-bright">Update & Notify Guest</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
